import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, LogIn } from 'lucide-react';
import '@/styles/backoffice-tokens.css';

const ROLE_ROUTES = {
  admin: '/ops/admin/dashboard',
  interviewer: '/ops/interviewer/dashboard',
  production: '/ops/production/dashboard',
};
const PRIORITY = ['admin', 'interviewer', 'production'];

function pickHighest(roles) {
  for (const r of PRIORITY) if (roles.includes(r)) return r;
  return null;
}

export default function OpsLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, user, setActiveRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.roles?.length) {
      const role = pickHighest(user.roles);
      if (role) navigate(ROLE_ROUTES[role]);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      const roles = data.roles || (data.role ? [data.role] : []);
      if (!roles.length) { setError('Account has no role assigned'); return; }
      const role = pickHighest(roles) || roles[0];
      setActiveRole(role);
      navigate(ROLE_ROUTES[role] || '/ops/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Login failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ops-shell min-h-screen flex items-center justify-center px-6 bg-[#FAFBFC]" data-testid="ops-login-page">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-7 h-7 rounded-md bg-slate-900 text-white grid place-items-center text-[12px] font-semibold tracking-tight">H</div>
          <span className="text-[14px] font-semibold text-slate-900 tracking-tight">Heirloom Ops</span>
        </div>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <div className="mb-5">
            <h1 className="text-[15px] font-semibold text-slate-900 tracking-tight">Sign in</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Internal operations only.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="ops-login-form">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                data-testid="ops-email-input"
                autoFocus
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
                required
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                data-testid="ops-password-input"
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
                required
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="ops-login-button"
              className="w-full h-9 inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
            >
              {loading
                ? <><Loader2 size={13} className="animate-spin" /> Signing in…</>
                : <><LogIn size={13} /> Sign in</>}
            </button>
          </form>
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-4">
          Need access? Contact your admin.
        </p>
      </div>
    </div>
  );
}
